import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import videojs from 'video.js';

import cx from '@strava/ui/clsx';

import { useFeedContext } from '../../../../../state/FeedContext';
import { useFeedEntityContext } from '../../../../../state/FeedEntityContext';

import styles from './VideoItem.scss';

const Poc = React.lazy(() => import('components/Poc'));
const Controls = React.lazy(() => import('./Controls'));

function VideoItem({
  media,
  className,
  onClick,
  onReady,
  playOnHover,
  isAutoPlayItem
}) {
  const {
    entityState: { isActivityVideoMuted }
  } = useFeedContext();

  const {
    entityState: { isAutoplayEnabled },
    entityState
  } = useFeedEntityContext();
  const [video, setVideo] = useState();
  const hoverTimerRef = useRef();

  function play() {
    clearTimeout(hoverTimerRef.current);

    hoverTimerRef.current = setTimeout(() => {
      // Pause all video that are currently playing
      videojs
        .getAllPlayers()
        .filter((p) => !p.paused() && p.id() !== video.id())
        .forEach((p) => p.pause());

      if (video && video.paused()) {
        video.play();
      }
    }, 500);
  }

  function pause() {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = null;

    if (video && !video.paused()) {
      video.pause();
    }
  }

  return (
    <div
      className={cx(styles.base, className)}
      onClick={() => {
        pause();
        onClick();
      }}
      onKeyPress={() => {
        pause();
        onClick();
      }}
      onMouseEnter={() => {
        if (!isAutoplayEnabled) return;
        if (!playOnHover) return;
        play();
        entityState.trackAnalytics({
          category: 'feed',
          page: 'activity',
          action: 'interact',
          element: 'video_hover_autoplay',
          properties: {
            video_id: media.photo_id,
            has_description: media.caption_escaped.length > 0,
            activity_athlete_id: media.owner_id
          }
        });
      }}
      onMouseLeave={() => {
        if (!isAutoplayEnabled) return;
        if (!playOnHover) return;
        if (isAutoPlayItem) return;
        pause();
      }}
      role="button"
      tabIndex="0"
    >
      <React.Suspense fallback={<div />}>
        <Controls
          duration={media.duration}
          player={video}
          isAutoplayEnabled={isAutoplayEnabled}
          videoId={media.photo_id}
        />
        <Poc
          media={media}
          onReady={(player) => {
            setVideo(player);
            onReady(player);
          }}
          options={{ controls: false, loop: true, muted: isActivityVideoMuted }}
          className={styles.video}
        />
      </React.Suspense>
    </div>
  );
}

VideoItem.defaultProps = {
  playOnHover: true,
  className: null,
  isAutoPlayItem: false,
  onClick: () => {},
  onReady: () => {}
};

VideoItem.propTypes = {
  media: PropTypes.shape({
    duration: PropTypes.number,
    photo_id: PropTypes.string,
    caption_escaped: PropTypes.string,
    owner_id: PropTypes.string
  }).isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func,
  onReady: PropTypes.func,
  playOnHover: PropTypes.bool,
  isAutoPlayItem: PropTypes.bool
};

export default VideoItem;
